import { Controller } from "@hotwired/stimulus"
import { disposeTooltip } from "../utils/bootstrap-tooltip.util"
import { isAbortError, rethrowUnlessAbort } from "../utils/fetch-error.util"
// getFormattedStreet is no longer imported here: main called it inline in this
// controller, but that comparison now lives in diffRegisterRecord, which calls it
// itself. Keeping the import would have left a dead reference behind the merge.
import { diffRegisterRecord } from "../utils/register-diff.util"

// Fallbacks for the server-side copy passed in through the `translations`
// value; a key the view did not send never reads as `undefined`.
const DEFAULT_TRANSLATIONS = {
  clientClarificationTranslation: "",
  nameLabel: "",
  streetLabel: "",
  postalCodeLabel: "",
  cityLabel: "",
  taxIdLabel: "",
  vatIdLabel: "",
  vatTooltipValid: "",
  vatTooltipValidName: "",
  vatTooltipInvalid: "",
  vatTooltipUnableToVerify: ""
}

// Connects to data-controller="company-details"
export default class extends Controller {
  static targets = [
    "name",
    "nameAbbreviation",
    "street",
    "postalCode",
    "city",
    "country",
    "registrationId",
    "taxId",
    "vatId",
    "registrationIdLabel",
    "taxIdLabel",
    "vatIdLabel",
    "registrationRow",
    "taxIdSeparator",
    "vatIdSeparator",
    "vatIcon",
    "clientWarning",
    "clientWarningList",
    "clientUpdateButton",
    "clientDescriptionDisplay"
  ]

  static values = {
    translations: { type: Object, default: {} },
    companies: Array,
    selectedCompanyId: Number,
    vatValidateUrl: String,
    updateClientUrl: String,
    registerApiUrl: String,
    supportedCountryId: { type: Number, default: 703 },
    // True for an issued document whose customer identity is frozen (pinned).
    // Keeps the server-rendered frozen values on load instead of overwriting
    // them from the current-client JSON.
    frozen: { type: Boolean, default: false }
  }

  difs = []
  selectedCompany = null
  // The in-flight VIES lookup; a newer selection or disconnect() aborts it.
  #vatAbort = null
  ignoredTargetsToClear = [
    "registrationIdLabel",
    "taxIdLabel",
    "vatIdLabel",
    "registrationRow",
    "taxIdSeparator",
    "vatIdSeparator",
    "vatIcon",
    "clientWarning",
    "clientWarningList",
    "clientUpdateButton"
  ]

  get #texts() {
    return { ...DEFAULT_TRANSLATIONS, ...this.translationsValue }
  }

  connect() {
    if (this.hasSelectedCompanyIdValue) {
      this.#selectedContactListener({
        detail: { value: this.selectedCompanyIdValue, preserveDisplay: this.frozenValue }
      })
      this._clientReferenceDateListener = () => this.#checkRegisterData(this.selectedCompany)
      // recurring documents does not have dates
      const referenceDateElement = this.#getReferenceCheckRegisterDataDate()
      if (referenceDateElement) {
        referenceDateElement.addEventListener("change", this._clientReferenceDateListener)
      }
    }

    this._selectedContactListener = this.#selectedContactListener.bind(this)
    document.addEventListener("selectedContact", this._selectedContactListener)

    if (this.hasNameAbbreviationTarget && this.nameAbbreviationTarget.innerText.trim() === "") {
      this.nameAbbreviationTarget.innerText = this.#getCompanyNameAbbreviation(this.nameTarget.innerText)
    }
  }

  disconnect() {
    this.#vatAbort?.abort()
    document.removeEventListener("selectedContact", this._selectedContactListener)
    if (this._clientReferenceDateListener) {
      document.removeEventListener("change", this._clientReferenceDateListener)
    }
    if (this._vatTooltip) {
      disposeTooltip(this._vatTooltip)
      this._vatTooltip = null
    }
  }

  #selectedContactListener(event) {
    const data = event.detail

    if (this.#isEmptyCompany(data)) {
      return
    }

    const selectedCompanyId = data.value
    this.selectedCompany = this.#findCompanyById(selectedCompanyId)

    // Issued document on load: keep the server-rendered FROZEN identity. Don't
    // clear/overwrite the display fields from the current-client JSON. (Only the
    // connect-time call sets preserveDisplay; user-initiated picks don't, so
    // re-selecting a client still updates the fields.)
    if (data.preserveDisplay) {
      return
    }

    this.#clearAllTargets()

    if (this.selectedCompany) {
      this.#setCompanyDetails(this.selectedCompany)
      this.#checkRegisterData(this.selectedCompany)
    }
  }

  #getReferenceCheckRegisterDataDate() {
    let element = document.querySelector('[id*="delivery_date"]')
    if (!element) {
      element = document.querySelector('[id*="issue_date"]')
    }
    return element
  }

  #setCompanyDetails(company) {
    if (this.hasNameTarget) {
      this.nameTarget.innerText = company.name
    }

    if (this.hasNameAbbreviationTarget) {
      this.nameAbbreviationTarget.innerText = this.#getCompanyNameAbbreviation(company.name)
    }

    if (this.hasStreetTarget) {
      this.streetTarget.innerText = company.street
    }

    if (this.hasPostalCodeTarget) {
      this.postalCodeTarget.innerText = company.postal_code
    }

    if (this.hasCityTarget) {
      this.cityTarget.innerText = company.city
    }

    if (this.hasCountryTarget) {
      this.countryTarget.innerText = company.country
    }

    const hasRegId = company.legal_registration_id?.trim()
    const hasTaxId = company.tax_id?.trim()
    const hasVatId = company.vat_id?.trim()
    const hasAnyRegistrationField = hasRegId || hasTaxId || hasVatId

    // Show/hide the entire registration row
    if (this.hasRegistrationRowTarget) {
      this.registrationRowTarget.classList.toggle("d-none", !hasAnyRegistrationField)
    }

    if (this.hasRegistrationIdTarget) {
      this.registrationIdTarget.innerText = company.legal_registration_id || ""
      if (this.hasRegistrationIdLabelTarget) {
        this.#hideLabelIfEmpty(company.legal_registration_id, this.registrationIdLabelTarget)
      }
    }

    if (this.hasTaxIdTarget) {
      this.taxIdTarget.innerText = company.tax_id || ""
      if (this.hasTaxIdLabelTarget) {
        this.#hideLabelIfEmpty(company.tax_id, this.taxIdLabelTarget)
      }
      // Show comma separator only if there's a preceding field
      if (this.hasTaxIdSeparatorTarget) {
        this.taxIdSeparatorTarget.classList.toggle("d-none", !hasRegId)
      }
    }

    if (this.hasVatIdTarget) {
      this.vatIdTarget.innerText = company.vat_id || ""
      if (this.hasVatIdLabelTarget) {
        if (hasVatId) {
          this.vatIdLabelTarget.className = "d-block d-sm-inline"
        } else {
          this.vatIdLabelTarget.className = "d-none"
        }
      }
      // Show comma separator only if there's a preceding field (responsive: hidden on xs)
      if (this.hasVatIdSeparatorTarget) {
        if (hasRegId || hasTaxId) {
          this.vatIdSeparatorTarget.className = "d-none d-sm-inline-block"
        } else {
          this.vatIdSeparatorTarget.className = "d-none"
        }
      }
      this.#validateVatId(company.vat_id)
    }

    // Reset VAT icon when no VAT ID
    if (!hasVatId && this.hasVatIconTarget) {
      this.vatIconTarget.classList.add("d-none")
    }
  }

  #findCompanyById(id) {
    const cid = parseInt(id, 10)
    return this.companiesValue.find((company) => company.id === cid)
  }

  #getCompanyNameAbbreviation(companyName) {
    return companyName?.charAt(0).toUpperCase()
  }

  #isEmptyCompany(company) {
    return company.text === "" && company.value === ""
  }

  #hideLabelIfEmpty(value, target) {
    if (!value || !value.trim()) {
      target.classList.add("d-none") // Hide the target element
    } else {
      target.classList.remove("d-none") // Show the target element
    }
  }

  #clearAllTargets(_ignoreTargets) {
    this.constructor.targets.forEach((targetName) => {
      const hasTargetMethod = `has${targetName.charAt(0).toUpperCase() + targetName.slice(1)}Target`
      if (this[hasTargetMethod] && !this.ignoredTargetsToClear.includes(targetName)) {
        this[`${targetName}Target`].innerText = ""
      }
    })
  }

  #validateVatId(vatId) {
    if (!this.hasVatIconTarget || !this.vatValidateUrlValue) return

    // A newer selection supersedes the lookup in flight: abort it, and should
    // its reply still land, the guards below keep it from repainting the icon
    // with the previous customer's verdict (stimulus-controllers.md §3–§4).
    this.#vatAbort?.abort()
    this.#vatAbort = new AbortController()
    const { signal } = this.#vatAbort

    if (!vatId) {
      this.vatIconTarget.classList.add("d-none")
      return
    }

    // Show loading spinner
    this.#setVatIcon("progress_activity", "selected-contact__vat-icon--loading", "")

    const url = `${this.vatValidateUrlValue}?vat_id=${encodeURIComponent(vatId)}`

    fetch(url, { headers: { Accept: "application/json" }, signal })
      .then((response) => response.json())
      .then((data) => {
        if (signal.aborted || !this.element.isConnected) return

        if (data.isValid === true) {
          const tooltipText = data.name
            ? this.#texts.vatTooltipValidName.replace("%{name}", this.#escapeHtml(data.name))
            : this.#texts.vatTooltipValid
          this.#setVatIcon("verified", "selected-contact__vat-icon--valid", tooltipText)
        } else if (data.isValid === false) {
          this.#setVatIcon("error", "selected-contact__vat-icon--invalid", this.#texts.vatTooltipInvalid)
        } else {
          // VIES did not look the number up (throttled, member state offline,
          // timed out): VatValidationService answers `isValid: null` with
          // `unverified: true`. That is not "invalid" — a red icon on a
          // registered customer's VAT id was exactly the bug this branch fixes.
          this.#setVatIcon("help", "selected-contact__vat-icon--warning", this.#texts.vatTooltipUnableToVerify)
        }
      })
      .catch((error) => {
        // An abort is the user moving on, never a rollback. Anything else is
        // shown as "could not verify" and re-raised for monitoring.
        if (!isAbortError(error)) {
          this.#setVatIcon("help", "selected-contact__vat-icon--warning", this.#texts.vatTooltipUnableToVerify)
        }
        rethrowUnlessAbort(error)
      })
  }

  #setVatIcon(icon, colorClass, tooltipText) {
    if (!this.hasVatIconTarget) return

    const target = this.vatIconTarget
    target.classList.remove("d-none")
    target.className = `material-symbols-outlined selected-contact__vat-icon ${colorClass}`
    target.textContent = icon

    // Clean up existing tooltip
    if (this._vatTooltip) {
      disposeTooltip(this._vatTooltip)
      this._vatTooltip = null
    }

    if (tooltipText) {
      // Dynamic import to avoid circular deps
      import("bootstrap").then(({ Tooltip }) => {
        this._vatTooltip = new Tooltip(target, {
          title: tooltipText,
          placement: "top",
          html: true
        })
      })
    }
  }

  async #checkRegisterData(company) {
    this.clientWarningTarget.classList.add("d-none")
    if (
      !this.hasClientWarningTarget ||
      !this.hasClientWarningListTarget ||
      !company ||
      company.country.id !== this.supportedCountryIdValue
    )
      return

    // Skip register check for physical persons
    // Physical persons have vat_payer_type = null and are not registered in business registers
    if (company.vat_payer_type === null || company.vat_payer_type === undefined) return

    const regId = company.legal_registration_id
    if (!regId) return
    const deliveryDate = this.#parseDate(
      this.#getReferenceCheckRegisterDataDate()?.value || new Date().toISOString().split("T")[0]
    )
    const url = `${this.registerApiUrlValue.replace("{param}", "id").replace("{encodedSearchValue}", encodeURIComponent(regId))}&historicData=true`
    try {
      const response = await fetch(url, { headers: { Accept: "application/json" } })
      if (!response.ok) return

      const data = await response.json() //fetch fist row
      const register = data[0]
      if (!register) return
      // TODO add check of debts

      const diffs = diffRegisterRecord({
        register,
        company,
        referenceDate: deliveryDate,
        labels: {
          name: this.#texts.nameLabel,
          street: this.#texts.streetLabel,
          postalCode: this.#texts.postalCodeLabel,
          city: this.#texts.cityLabel,
          taxId: this.#texts.taxIdLabel,
          vatId: this.#texts.vatIdLabel
        }
      })

      if (diffs.length === 0) return

      let html = ""
      diffs.forEach((d) => {
        html += `<li>${this.#escapeHtml(d.label)}: <s>${this.#escapeHtml(d.current || "")}</s> → <strong>${this.#escapeHtml(d.register || "")}</strong></li>`
      })
      this.clientWarningListTarget.innerHTML = html
      this.clientWarningTarget.classList.remove("d-none")
      this.clientDescriptionDisplayTarget.innerHTML = this.#texts.clientClarificationTranslation
        .replace("{delivery_date}", this.#escapeHtml(this.#getReferenceCheckRegisterDataDate().value))
        .replace("{legal_registration_id}", this.#escapeHtml(company.legal_registration_id))

      if (company.contact_id) {
        this.diffs = diffs
        this.clientUpdateButtonTarget.classList.remove("d-none")
        this.clientUpdateButtonTarget.setAttribute("data-contact-id", company.contact_id)
      } else {
        // situation when we display document that do not have client in accounts - he was removed meantime
        this.clientUpdateButtonTarget.classList.add("d-none")
      }
    } catch (e) {
      console.error(e)
    }
  }

  updateWithRegister(event) {
    const clientId = event.currentTarget.getAttribute("data-contact-id")
    if (!clientId || !this.updateClientUrlValue) return

    const url = this.updateClientUrlValue.replace("/0/", `/${clientId}/`)
    // Prepare patch data as a hashmap of name: register
    const patchData = {}
    if (this.diffs && Array.isArray(this.diffs)) {
      this.diffs.forEach((diff) => {
        if (diff.name && diff.register !== undefined) {
          patchData[diff.name] = diff.register
        }
      })
    }
    fetch(url, {
      method: "PATCH",
      headers: {
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
        Accept: "text/vnd.turbo-stream.html",
        "Content-Type": "application/json"
      },
      body: JSON.stringify(patchData)
    })
      .then((resp) => resp.text())
      .then((html) => {
        window.Turbo.renderStreamMessage(html)
      })
      .catch(rethrowUnlessAbort)
  }

  #parseDate(dateStr) {
    if (!dateStr) return null
    return new Date(dateStr)
  }

  #escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
